import { getSegmentedRule, RuleRow } from "./parseRules";
import { validateRule } from "./ruleValidator";
const QUOTE = '"';

// The ini reader treats "=", " is " and " is for any " as key delimiters and skips any
// occurrence followed by an odd number of quotes (MadMilkman IniReader.ReadKey). A value
// therefore only needs quoting when it contains one of those tokens - a plain space does not
// confuse the parser.
const KEY_DELIMITERS = /(=| is for any | is )/gi;

// Optional separator: a command with nothing after the prefix has to match too.
const RUN_COMMAND_PREFIX = /^(Pre|Post)-run Command(\s+|$)/i;

export const isRunCommandName = (name?: string | null): boolean =>
  !!name && /^(pre|post)-run command$/i.test(name.trim());

const splitOnKeyDelimiter = (line: string): { name: string; value: string; delimiter: string } | null => {
  KEY_DELIMITERS.lastIndex = 0;
  let match = KEY_DELIMITERS.exec(line);

  while (match) {
    const rest = line.slice(match.index + match[0].length);
    if ((rest.match(/"/g) || []).length % 2 === 0) {
      return { name: line.slice(0, match.index).trim(), value: rest.trim(), delimiter: match[0].trim() };
    }
    KEY_DELIMITERS.lastIndex = match.index + 1;
    match = KEY_DELIMITERS.exec(line);
  }

  return null;
};

export interface ParsedConfigLine {
  name: string;
  value: string;
  delimiter: string;
  applicability: string;
  inlineComment: string;
}

// Reads a plain configuration line back into its parts, the way the ini reader and the server
// do. Returns null when the text cannot be recovered as a configuration, which is what tells
// the UI that a commented row is not safe to uncomment.
export const parseConfigLine = (content: string): ParsedConfigLine | null => {
  const text = (content ?? "").trim().replace(/^;+\s*/, "");
  if (!text) return null;

  const split = splitOnKeyDelimiter(text);
  if (!split) return null;

  // A run command is named by two words, so it cannot be split on the first space the way an
  // argument is. Getting this wrong leaves a row named "Post-run", which stops matching the
  // name comparisons that make it behave as a command.
  const runCommand = split.name.match(RUN_COMMAND_PREFIX);
  const firstSpace = split.name.indexOf(" ");

  const name = runCommand
    ? `${/^pre/i.test(runCommand[0]) ? "Pre" : "Post"}-run Command`
    : (firstSpace === -1 ? split.name : split.name.slice(0, firstSpace)).trim();
  if (!name) return null;

  const trailingComment = split.value.match(/^(.*?)\s*;(.+)$/);

  return {
    name,
    value: runCommand
      ? split.name.slice(runCommand[0].length).trim()
      : firstSpace === -1
      ? ""
      : split.name.slice(firstSpace + 1).trim(),
    delimiter: split.delimiter,
    applicability: (trailingComment ? trailingComment[1] : split.value).trim(),
    inlineComment: trailingComment ? trailingComment[2].trim() : "",
  };
};

// Structural parsing is not enough to know a commented line can go back to being a rule: the
// applicability has to hold up too, otherwise uncommenting produces a live rule that is wrong
// and nothing outside the edit dialog would say so.
export const getConfigRuleError = (delimiter?: string | null, applicability?: string | null): string | null => {
  const expression = `${delimiter ?? ""} ${applicability ?? ""}`.trim();
  if (!expression) return null;

  const rows: RuleRow[] = getSegmentedRule(expression).map((segment) => ({
    delimiter: segment.delimiter,
    alias: segment.alias,
    property: segment.property,
    operator: segment.operator,
    value: segment.value,
  }));

  const result = validateRule(rows);
  return result.isValid ? null : result.errors[0] ?? "The applicability is not valid.";
};

export const getConfigLineError = (content: string): string | null => {
  const parsed = parseConfigLine(content);
  if (!parsed) return "The comment is not a configuration line.";
  return getConfigRuleError(parsed.delimiter, parsed.applicability);
};

export const isValidConfigLine = (content: string): boolean => getConfigLineError(content) === null;

const stripOneQuotePair = (value: string): string =>
  value.length >= 2 && value.startsWith(QUOTE) && value.endsWith(QUOTE) ? value.slice(1, -1) : value;

export const unwrapOuterQuotes = (value: string): string => stripOneQuotePair((value ?? "").trim());

// Comment rows carry the whole raw line in value and must never be quoted, otherwise the
// writer stops recognising them and emits them as live configuration.
export const isCommentRow = (name?: string | null, value?: string | null, isComment?: boolean): boolean =>
  !!isComment || !!name?.trim().startsWith("-comment-") || !!name?.trim().startsWith(";") || !!value?.trim().startsWith(";");

// AidenBot recovers an argument as everything after the first space of the key name, and a
// command as the key name minus the prefix, minus at most one enclosing quote pair
// (PolicySet.GetPackageConfigurationArguments / GetConfigurationActions).
const recoverValue = (keyName: string, isCommand: boolean): string | null => {
  if (isCommand) {
    const prefix = keyName.match(RUN_COMMAND_PREFIX);
    if (!prefix) return null;
    return stripOneQuotePair(keyName.slice(prefix[0].length));
  }

  const firstSpace = keyName.indexOf(" ");
  return firstSpace === -1 ? "" : keyName.slice(firstSpace + 1).trim();
};

const roundTrips = (
  name: string,
  value: string,
  delimiter: string,
  applicability: string,
  isCommand: boolean
): boolean => {
  const split = splitOnKeyDelimiter(`${name} ${value}   ${delimiter} ${applicability}`);
  if (!split || split.value !== applicability.trim()) return false;

  return recoverValue(split.name, isCommand) === (isCommand ? stripOneQuotePair(value) : value);
};

// Returns the value as it should be written, or null when no representation survives the
// round trip and the user has to fix it. Leaves anything that already parses correctly
// byte-identical, so opening a policy and saving it without edits produces no diff.
export const normalizeConfigValue = (
  name: string,
  value: string,
  delimiter: string,
  applicability: string
): string | null => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return trimmed;
  if (isCommentRow(name, trimmed)) return trimmed;

  const isCommand = isRunCommandName(name);
  const safeDelimiter = (delimiter || "is for any").trim();
  const safeApplicability = (applicability || "").trim();

  if (roundTrips(name, trimmed, safeDelimiter, safeApplicability, isCommand)) return trimmed;

  const wrapped = `${QUOTE}${trimmed}${QUOTE}`;
  if (roundTrips(name, wrapped, safeDelimiter, safeApplicability, isCommand)) return wrapped;

  return null;
};

// A run command is written in quotes by convention (AB#3399). Applied only when the user
// authors or edits a rule, never to untouched lines, and skipped when the value already has a
// quote at either end, where a wrapper would read as a doubled quote.
export const normalizeAuthoredConfigValue = (
  name: string,
  value: string,
  delimiter: string,
  applicability: string
): string | null => {
  const normalized = normalizeConfigValue(name, value, delimiter, applicability);
  if (!normalized || !isRunCommandName(name)) return normalized;
  if (normalized.startsWith(QUOTE) || normalized.endsWith(QUOTE)) return normalized;

  const wrapped = `${QUOTE}${normalized}${QUOTE}`;
  return normalizeConfigValue(name, wrapped, delimiter, applicability) === wrapped ? wrapped : normalized;
};

export const getConfigValueError = (
  name: string,
  value: string,
  delimiter: string,
  applicability: string
): string | null =>
  normalizeConfigValue(name, value, delimiter, applicability) === null
    ? "This value cannot be saved as written. Balance the double quotes, or wrap the whole value in quotes, so the rule can be read back correctly."
    : null;
